"use client";

import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../../../context/AuthContext";
import modalStyles from "../../../../css/modal.module.css";
import detailStyles from "../../css/inventoryItemDetail.module.css";
import Button from "../../../../components/Button";
import Dropdown from "../../../../components/Dropdown";
import UsageModal from "../views/UsageModal.jsx";
import DeclineModal from "../views/DeclineModal.jsx";
import StockRequestFrontShopForm from "./StockRequestFrontShopForm.jsx";
import StockRequestClinicForm from "./StockRequestClinicForm.jsx";
import StockRequestOtherForm from "./StockRequestOtherForm.jsx";
import layoutStyles from "../css/stockRequestComponents.module.css";
import { computeClosestFutureExpiry } from "./stockRequestUtils.jsx";
import { fetchStockAvailability, fetchHighestQuantityBranch } from "../../../../api/Inventory/StockRequestApi";

export default function StockRequestModal({
  isOpen,
  context,
  selectedRow,
  activeSegment,
  mode,
  branches,
  onSubmitRequest,
  onApprove,
  onDecline,
  onClose,
}) {
  const { user } = useAuth();
  const [isRequestFlow, setIsRequestFlow] = useState(false);
  const [requestType, setRequestType] = useState("");
  const isReviewMode = mode === "Review";

  const [quantity, setQuantity] = useState(1);
  const [productName, setProductName] = useState("");
  const [branchDisplay, setBranchDisplay] = useState("");
  const [sku, setSku] = useState("");
  const [category, setCategory] = useState("");
  const [strength, setStrength] = useState("");
  const [packSize, setPackSize] = useState("");
  const [cost, setCost] = useState("");
  const [expiryDate, setExpiryDate] = useState(null);
  const [dateRequested, setDateRequested] = useState(new Date());
  const [requestedBy, setRequestedBy] = useState("");
  const [note, setNote] = useState("");
  const [barcode, setBarcode] = useState("");
  const [nappiCode, setNappiCode] = useState("");
  const [activeIngredient, setActiveIngredient] = useState("");

  const [showUsageModal, setShowUsageModal] = useState(false);
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [stockQty, setStockQty] = useState(null);
  const [showStockPill, setShowStockPill] = useState(false);

  useEffect(() => {
    const fullName = [user?.firstname || user?.name, user?.surname]
      .filter(Boolean)
      .join(" ");
    setRequestedBy(fullName || "");
    const branch = user?.branchcode || user?.branch;
    setBranchDisplay(branch ? String(branch) : "");
  }, [user?.firstname, user?.name, user?.surname, user?.branchcode, user?.branch]);

  useEffect(() => {
    if (!isOpen) return;

    if (context === "request") {
      setIsRequestFlow(true);
      setRequestType("");
      setSelectedFromRow(null);
      setProductName("");
      setSku("");
      setCategory("");
      setStrength("");
      setPackSize("");
      setCost("");
      setExpiryDate(null);
      setNote("");
      setQuantity(1);
      setDateRequested(new Date());
      setBarcode("");
      setNappiCode("");
      setActiveIngredient("");
    } else if (selectedRow) {
      setIsRequestFlow(false);
      setRequestType("");
      setProductName(selectedRow.drugName || "");
      setQuantity(selectedRow.requestedQty || 1);
      setNote(selectedRow.note || "");
    }
  }, [isOpen, context, selectedRow]);

  const [selectedFromRow, setSelectedFromRow] = useState(null);

  useEffect(() => {
    if (!isOpen) return;

    const originalOverflow = document.body.style.overflow || "";
    const originalHtmlOverflow = document.documentElement.style.overflow || "";
    const rootElement = document.getElementById("root");
    const originalRootOverflow = rootElement ? rootElement.style.overflow || "" : "";

    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    if (rootElement) rootElement.style.overflow = "hidden";

    const blockScroll = (e) => {
      e.preventDefault();
    };

    window.addEventListener("wheel", blockScroll, { passive: false });
    window.addEventListener("touchmove", blockScroll, { passive: false });

    return () => {
      document.body.style.overflow = originalOverflow;
      document.documentElement.style.overflow = originalHtmlOverflow;
      if (rootElement) rootElement.style.overflow = originalRootOverflow;
      window.removeEventListener("wheel", blockScroll, { passive: false });
      window.removeEventListener("touchmove", blockScroll, { passive: false });
    };
  }, [isOpen]);

  const segmentForModal = useMemo(() => {
    if (isRequestFlow) {
      if (requestType === "Item") return "Front Shop";
      if (requestType === "Drug") return "Drug";
      return activeSegment;
    }
    const rowType = selectedRow?.type;
    if (rowType === "Item") return "Front Shop";
    if (rowType === "Drug") return "Drug";
    return activeSegment;
  }, [isRequestFlow, requestType, activeSegment, selectedRow?.type]);

  const handleProductSelect = async (medication) => {
    if (!medication) return;
    
    // Set product details first
    setProductName(medication.name || "");
    const rawSku =
      medication.sku ??
      medication.SKU ??
      medication.drugSku ??
      medication.drugsku ??
      (medication.drug && (medication.drug.sku || medication.drug.SKU)) ??
      (medication.inventory && (medication.inventory.sku || medication.inventory.SKU));
    if (rawSku != null) {
      setSku(String(rawSku));
    }
    setCategory(
      medication.categoryName ||
        medication.category ||
        medication.itemCategory ||
        (medication.item && (medication.item.category || medication.item["category"])) ||
        ""
    );
    setStrength(medication.strength || "");
    const ps =
      medication.packSize ??
      medication.packsize ??
      (medication.drug && (medication.drug.packsize || medication.drug.packSize)) ??
      (medication.item && (medication.item.packsize || medication.item.packSize)) ??
      (medication.inventory && (medication.inventory.packsize || medication.inventory.packSize));
    setPackSize(ps != null ? String(ps) : "");
    const cp =
      medication.costPrice ??
      medication.costprice ??
      (medication.inventory && (medication.inventory.costprice || medication.inventory.costPrice));
    setCost(cp != null ? String(cp) : "");
    const expDates =
      medication.expiryDates ??
      medication.expirydates ??
      (medication.drug && (medication.drug.expiryDates || medication.drug.expirydates)) ??
      (medication.item && (medication.item.expiryDates || medication.item.expirydates)) ??
      (medication.inventory && (medication.inventory.expiryDates || medication.inventory.expirydates)) ??
      [];
    const closest = computeClosestFutureExpiry(expDates);
    setExpiryDate(closest);

    const nappi =
      medication.nappiCode ??
      medication.nappicode ??
      medication.nappi ??
      (medication.drug && (medication.drug.nappicode || medication.drug.nappiCode)) ??
      (medication.inventory && (medication.inventory.nappicode || medication.inventory.nappiCode));
    if (nappi != null) setNappiCode(String(nappi));

    const active =
      medication.activeIngredient ??
      medication.activeingredient ??
      (medication.drug && (medication.drug.activeingredient || medication.drug.activeIngredient)) ??
      (medication.inventory && (medication.inventory.activeingredient || medication.inventory.activeIngredient));
    setActiveIngredient(active || "");

    // Auto-select branch with highest quantity
    try {
      const medicationName = medication.name || "";
      const medicationSku = rawSku != null ? String(rawSku) : "";
      
      const highestQuantityData = await fetchHighestQuantityBranch({
        sku: medicationSku,
        name: medicationName
      });
      
      if (highestQuantityData && highestQuantityData.branchCode) {
        // Find the branch name from branches array
        const branch = branches.find(b => 
          (b.branchcode === highestQuantityData.branchCode) || 
          (b.branchCode === highestQuantityData.branchCode) ||
          (b.code === highestQuantityData.branchCode)
        );
        
        if (branch) {
          const branchName = branch.branchname || branch.branchName || branch.name || "";
          setBranchDisplay(branchName);
          console.log(`Auto-selected branch with highest quantity: ${branchName} (${highestQuantityData.quantity} units)`);
        } else {
          // Fallback to original logic if branch not found
          if (medication.branchName || medication.branchCode) {
            setBranchDisplay(String(medication.branchName || medication.branchCode));
          }
        }
      } else {
        // Fallback to original logic if no highest quantity found
        if (medication.branchName || medication.branchCode) {
          setBranchDisplay(String(medication.branchName || medication.branchCode));
        }
      }
    } catch (error) {
      console.error("Error finding highest quantity branch:", error);
      // Fallback to original logic
      if (medication.branchName || medication.branchCode) {
        setBranchDisplay(String(medication.branchName || medication.branchCode));
      }
    }
  };

  const branchOptions = useMemo(
    () =>
      branches
        .map((b) => b.branchname || b.branchName || b.name || "")
        .filter((v) => typeof v === "string" && v.trim().length > 0),
    [branches]
  );

  useEffect(() => {
    if (!isOpen || !isRequestFlow) {
      setStockQty(null);
      setShowStockPill(false);
      return;
    }

    const hasProduct =
      (sku && String(sku).trim().length > 0) ||
      (productName && productName.trim().length > 0);

    if (!hasProduct || !branchDisplay) {
      setStockQty(null);
      setShowStockPill(false);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const data = await fetchStockAvailability({
          sku: sku && String(sku).trim().length > 0 ? sku : undefined,
          name:
            (!sku || String(sku).trim().length === 0) && productName
              ? productName
              : undefined,
          branch: branchDisplay,
        });
        if (cancelled) return;
        if (data && data.quantity != null) {
          setStockQty(data.quantity);
          setShowStockPill(true);
        } else {
          setStockQty(null);
          setShowStockPill(false);
        }
      } catch {
        if (cancelled) return;
        setStockQty(null);
        setShowStockPill(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, isRequestFlow, sku, productName, branchDisplay]);

  if (!isOpen) return null;

  return (
    <div id="modal-popup" className={modalStyles.modalPopup}>
      <div className={modalStyles.modalContent} onClick={(e) => e.stopPropagation()}>
        {!(isRequestFlow && !requestType) && (
          <button
            className={modalStyles.closeBtn}
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        )}
        <div
          className={`${detailStyles.card} ${
            isRequestFlow && !requestType ? detailStyles.narrowRequestCard : ""
          }`}
        >
          {isRequestFlow && !requestType ? (
            <div className={detailStyles.row}>
              <div className={`${detailStyles.field} ${detailStyles.centerRequestField}`}>
                <label>What would you like to request?</label>
                <Dropdown
                  options={["Item", "Drug"]}
                  value={requestType || ""}
                  onChange={(val) =>
                    setRequestType(typeof val === "string" ? val : val?.value || "")
                  }
                  className={detailStyles.centerRequestSelect}
                  style={{ width: "180px" }}
                />
              </div>
            </div>
          ) : segmentForModal === "Front Shop" ? (
            <StockRequestFrontShopForm
              isRequestFlow={isRequestFlow}
              isReviewMode={isReviewMode}
              selectedRow={selectedRow}
              activeSegment={activeSegment}
              branchOptions={branchOptions}
              branchDisplay={branchDisplay}
              setBranchDisplay={setBranchDisplay}
              sku={sku}
              setSku={setSku}
              category={category}
              setCategory={setCategory}
              packSize={packSize}
              setPackSize={setPackSize}
              cost={cost}
              setCost={setCost}
              expiryDate={expiryDate}
              setExpiryDate={setExpiryDate}
              dateRequested={dateRequested}
              setDateRequested={setDateRequested}
              requestedBy={requestedBy}
              setRequestedBy={setRequestedBy}
              note={note}
              setNote={setNote}
              quantity={quantity}
              setQuantity={setQuantity}
              onSubmitRequest={onSubmitRequest}
              productName={productName}
              handleProductSelect={handleProductSelect}
              stockQty={stockQty}
              showStockPill={showStockPill}
              onHideStockPill={() => setShowStockPill(false)}
            />
          ) : segmentForModal === "Clinic" ? (
            <StockRequestClinicForm
              isRequestFlow={isRequestFlow}
              selectedRow={selectedRow}
              activeSegment={activeSegment}
              branchOptions={branchOptions}
              branchDisplay={branchDisplay}
              setBranchDisplay={setBranchDisplay}
              sku={sku}
              setSku={setSku}
              barcode={barcode}
              setBarcode={setBarcode}
              strength={strength}
              setStrength={setStrength}
              packSize={packSize}
              setPackSize={setPackSize}
              cost={cost}
              setCost={setCost}
              expiryDate={expiryDate}
              setExpiryDate={setExpiryDate}
              dateRequested={dateRequested}
              setDateRequested={setDateRequested}
              requestedBy={requestedBy}
              setRequestedBy={setRequestedBy}
              note={note}
              setNote={setNote}
              quantity={quantity}
              setQuantity={setQuantity}
              onSubmitRequest={onSubmitRequest}
              productName={productName}
              handleProductSelect={handleProductSelect}
              stockQty={stockQty}
              showStockPill={showStockPill}
              onHideStockPill={() => setShowStockPill(false)}
            />
          ) : (
            <StockRequestOtherForm
              isRequestFlow={isRequestFlow}
              isReviewMode={isReviewMode}
              selectedRow={selectedRow}
              activeSegment={activeSegment}
              branchOptions={branchOptions}
              branchDisplay={branchDisplay}
              setBranchDisplay={setBranchDisplay}
              sku={sku}
              setSku={setSku}
              nappiCode={nappiCode}
              setNappiCode={setNappiCode}
              strength={strength}
              setStrength={setStrength}
              activeIngredient={activeIngredient}
              setActiveIngredient={setActiveIngredient}
              packSize={packSize}
              setPackSize={setPackSize}
              cost={cost}
              setCost={setCost}
              expiryDate={expiryDate}
              setExpiryDate={setExpiryDate}
              dateRequested={dateRequested}
              setDateRequested={setDateRequested}
              requestedBy={requestedBy}
              setRequestedBy={setRequestedBy}
              note={note}
              setNote={setNote}
              quantity={quantity}
              setQuantity={setQuantity}
              onSubmitRequest={onSubmitRequest}
              productName={productName}
              handleProductSelect={handleProductSelect}
              stockQty={stockQty}
              showStockPill={showStockPill}
              onHideStockPill={() => setShowStockPill(false)}
            />
          )}

          {!isRequestFlow && isReviewMode && selectedRow && (Number(user?.roleid) === 3 || Number(user?.roleid) === 8) && selectedRow.status === "Pending" && (
            <div className={layoutStyles.modalActionsRow}>
              <Button variant="primary" onClick={() => setShowUsageModal(true)}>
                View Usage
              </Button>
              <Button
                variant="secondary"
                onClick={() => setShowDeclineModal(true)}
              >
                Decline
              </Button>
              <Button
                variant="approve"
                onClick={async () => {
                  if (onApprove && selectedRow.requestId) {
                    await onApprove(selectedRow.requestId);
                  }
                  onClose();
                }}
              >
                Approve
              </Button>
            </div>
          )}
        </div>
      </div>

      {showUsageModal && (
        <UsageModal
          title={`Average stock usage for: ${selectedRow?.drugName || "Item"}`}
          sku={selectedRow?.sku ? String(selectedRow.sku) : sku || ""}
          branch={branchDisplay}
          user={user}
          onClose={() => setShowUsageModal(false)}
        />
      )}

      {showDeclineModal && (
        <DeclineModal
          onClose={() => setShowDeclineModal(false)}
          onSubmit={async (reason) => {
            if (onDecline && selectedRow?.requestId) {
              await onDecline(selectedRow.requestId, reason);
            }
            setShowDeclineModal(false);
            onClose();
          }}
        />
      )}
    </div>
  );
}
